import {
  isArray,
  isCustomer,
  isString,
  isStringEmpty,
} from "../utils/checkTool";
import request, { AxiosResponseBody } from ".";
import { cleanToCustomer } from "../utils/cleanData";
import { Customer, CustomerAdd, PriceHis, Price } from "@/types/sales";
import { DictLike } from "@/types/dict";
import { CommonListParams } from "@/types/common";

const prefixUrl = "/wms/customer";

const apis = {
  add: prefixUrl + `/add`,
  get: (id: string) => prefixUrl + `/${id}/get`,
  fetch: (id: string) => prefixUrl + `/${id}/fetch`,
  del: (id: string) => prefixUrl + `/${id}/del`,
  list: (orgId?: string) => `${prefixUrl}/${orgId ? orgId + '/' : ''}list`,
  fetchs: prefixUrl + `/fetchs`,
  update: (id: string) => prefixUrl + `/${id}/update`,
  updateByKey: (id: string, key: CustomerUpdateKey) =>
    prefixUrl + `/${id}/${key}/update`,
  creditLevelTypes: prefixUrl + "/creditLevel/types",
  currencyTypes: "wms/sale/currency/types",

  latestPrices: (id: string) => prefixUrl + `/${id}/latest/his/list`,
  prices: (id: string) => prefixUrl + `/${id}/price/list`,
};

/**
 * name1: 客户名称1
 *
 * name2: 客户名称2
 *
 * shortName: 客户简称
 *
 * address: 客户地址
 *
 * zipCode: 客户邮编
 *
 * email: 客户电子邮箱
 *
 * contact: 联系人
 *
 * phone: 联系电话
 *
 * mobile: 联系手机
 *
 * fax: 传真
 *
 * account1: 开户银行1
 *
 * bank1: 银行帐号1
 *
 * swiftCode1: 银行编码1
 *
 * account2: 开户银行2
 *
 * bank2: 银行帐号2
 *
 * swiftCode2: 银行编码2
 *
 * currency: 币种
 *
 * saleType: 销售类型
 *
 * creditLevel: 信用等级
 *
 * creditLine: 信用额度
 *
 * depositRate: 定金比例
 *
 * tradeTerm: 贸易条款
 *
 * payMethod: 付款方式
 *
 * commissionRate: 佣金比例
 *
 * affiliates: 关联公司
 *
 * orgId: 分管部门
 *
 * docId: 文档ID
 *
 * country: 国家/地区
 *
 * billingDays: 帐期
 *
 * principal: 委托方
 *
 * taxRate: 税率
 */
type CustomerUpdateKey =
  | "name1"
  | "name2"
  | "shortName"
  | "address"
  | "zipCode"
  | "email"
  | "contact"
  | "phone"
  | "mobile"
  | "fax"
  | "bank1"
  | "account1"
  | "swiftCode1"
  | "bank2"
  | "account2"
  | "swiftCode2"
  | "currency"
  | "saleType"
  | "creditLevel"
  | "creditLine"
  | "depositRate"
  | "tradeTerm"
  | "payMethod"
  | "commissionRate"
  | "affiliates"
  | "org"
  | "docId"
  | "country"
  | "billingDays"
  | "principal"
  | "taxRate";

export const list = (orgId?: string, params?: CommonListParams<string>, supplierApi?: string) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Customer>, string>(apis.list(orgId), { params, requestConfig: supplierApi ? { baseURL: supplierApi } : undefined })
    .then(({ body: customer, pagination }) => {
      if (isArray<Customer>(customer)) {
        return Promise.resolve({
          body: customer
            .filter((customer) => isCustomer(customer))
            ?.map((customer) => cleanToCustomer(customer)),
          pagination,
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const fetchs = (params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Customer>, string>(apis.fetchs, { params })
    .then(({ body: customer }) => {
      if (isArray<Customer>(customer)) {
        return Promise.resolve({
          body: customer
            .filter((customer) => isCustomer(customer))
            ?.map((customer) => cleanToCustomer(customer)),
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const add = (customer: CustomerAdd) => {
  if (customer.code && !isString(customer.code)) {
    return Promise.reject(10012);
  }
  return request<Customer, CustomerAdd>(apis.add, {
    params: { body: customer },
  })
    .then(({ body: customer }) => {
      if (isCustomer(customer)) {
        return Promise.resolve(cleanToCustomer(customer));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const del = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void, void>(apis.del(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const get = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<Customer, string>(apis.get(id))
    .then(({ body: customer }) => {
      if (isCustomer(customer)) {
        return Promise.resolve(cleanToCustomer(customer));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const fetch = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<Customer, string>(apis.fetch(id))
    .then(({ body: customer }) => {
      if (isCustomer(customer)) {
        return Promise.resolve(cleanToCustomer(customer));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export interface UpdateByKey {
  key: CustomerUpdateKey;
  value: string;
}

export const updateByKey = (id: string, props: UpdateByKey) => {
  return new Promise<void>((resolve, reject) => {
    if (
      (!isString(id) || isStringEmpty(id)) &&
      props.key &&
      !isString(props.key)
    ) {
      return reject(10012);
    }
    request<void, string>(apis.updateByKey(id, props.key), {
      params: { body: props.value },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const update: (props: CustomerAdd, id: string) => Promise<void> = (
  props,
  id
) => {
  return new Promise((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id) && props.code && !isString(props.code))
    ) {
      return reject(10012);
    }
    request<void, CustomerAdd>(apis.update(id), {
      params: { body: props },
    })
      .then(() => resolve())
      .catch(() => reject());
  });
};

export const creditLevels = () => {
  return new Promise<Array<DictLike>>((resolve, reject) => {
    request<Array<DictLike>, string>(apis.creditLevelTypes)
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const currencyTypes = () => {
  return new Promise<Array<DictLike>>((resolve, reject) => {
    request<Array<DictLike>, void>(apis.currencyTypes)
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const latestPrices = (id: string, params?: CommonListParams<string>, supplierApi?: string) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<PriceHis>, string>(apis.latestPrices(id), { params, requestConfig: supplierApi ? { baseURL: supplierApi } : undefined })
    .then(({ body: price, pagination }) => {
      if (isArray<PriceHis>(price)) {
        return Promise.resolve({
          body: price,
          pagination,
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const prices: (
  id: string,
  params?: CommonListParams<string>
) => Promise<AxiosResponseBody<Array<Price>>> = (id, params) => {
  return new Promise((resolve, reject) => {
    request<Array<Price>, string>(apis.prices(id), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const exportObject = {
  list,
  fetchs,
  add,
  del,
  get,
  fetch,
  updateByKey,
  update,
  creditLevels,
  currencyTypes,
  latestPrices,
  prices
};

export type { CustomerUpdateKey };

export default exportObject;
